/**
 * Social Media Share URLs
 * These are static URL patterns for sharing content on social media platforms.
 * Stored in frontend as they don't change and don't require API calls.
 */

export interface SocialShareOptions {
  text?: string;
  url?: string;
}

/**
 * Get X (formerly Twitter) share URL
 */
export const getTwitterShareUrl = (options: SocialShareOptions): string => {
  const { text = '', url = '' } = options;
  const shareText = text ? `${text}${url ? ' ' : ''}` : '';
  const params = new URLSearchParams();
  if (shareText) params.append('text', shareText);
  if (url) params.append('url', url);
  return `https://x.com/intent/tweet?${params.toString()}`;
};

/**
 * Get Facebook share URL
 */
export const getFacebookShareUrl = (options: SocialShareOptions): string => {
  const { url = '' } = options;
  const params = new URLSearchParams();
  if (url) params.append('u', url);
  return `https://www.facebook.com/sharer/sharer.php?${params.toString()}`;
};

/**
 * Get LinkedIn share URL
 */
export const getLinkedInShareUrl = (options: SocialShareOptions): string => {
  const { text = '', url = '' } = options;
  const params = new URLSearchParams();
  if (url) params.append('url', url);
  if (text) params.append('summary', text);
  return `https://www.linkedin.com/sharing/share-offsite/?${params.toString()}`;
};

/**
 * Get Instagram share URL
 * Instagram doesn't have a direct web share URL, so we open Instagram's website
 * The content is already copied to clipboard by shareToPlatform
 */
export const getInstagramShareUrl = (_options: SocialShareOptions): string => {
  // Instagram doesn't support direct web sharing via URL parameters
  // Content is copied to clipboard before opening Instagram
  return 'https://www.instagram.com/';
};

/**
 * Get TikTok share URL
 */
export const getTikTokShareUrl = (options: SocialShareOptions): string => {
  const { text = '', url = '' } = options;
  const shareText = text && url ? `${text} ${url}` : text || url;
  const params = new URLSearchParams();
  if (shareText) params.append('text', shareText);
  // TikTok doesn't have a direct web share URL, but we can open the website
  return 'https://www.tiktok.com/';
};

/**
 * Get YouTube share URL
 */
export const getYouTubeShareUrl = (options: SocialShareOptions): string => {
  const { text = '', url = '' } = options;
  const shareText = text && url ? `${text} ${url}` : text || url;
  const params = new URLSearchParams();
  if (shareText) params.append('text', shareText);
  // YouTube doesn't have a direct web share URL, but we can open the website
  return 'https://www.youtube.com/';
};

/**
 * Get Pinterest share URL
 */
export const getPinterestShareUrl = (options: SocialShareOptions): string => {
  const { text = '', url = '' } = options;
  const params = new URLSearchParams();
  if (url) params.append('url', url);
  if (text) params.append('description', text);
  return `https://pinterest.com/pin/create/button/?${params.toString()}`;
};

/**
 * Open share URL in a new window
 */
export const openShareUrl = (url: string): void => {
  window.open(url, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
};

/**
 * Check if a platform is a social media platform (not email/blog)
 * Based on the actual platforms in the system:
 * - instagram, facebook, twitter, linkedin, tiktok, youtube, pinterest (social media - show share)
 * - email, blog (NOT social media - hide share)
 */
export const isSocialMediaPlatform = (platform: string): boolean => {
  const socialPlatforms = [
    'instagram',
    'facebook',
    'twitter',
    'x',
    'linkedin',
    'tiktok',
    'youtube',
    'pinterest',
  ];
  return socialPlatforms.includes(platform.toLowerCase());
};

/**
 * Copy text to clipboard
 */
const copyToClipboard = async (text: string): Promise<void> => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  } catch (err) {
    // console.warn('Failed to copy to clipboard:', err);
  }
};

/**
 * Share content to a specific platform
 * Only supports the actual platforms in the system:
 * instagram, facebook, twitter, linkedin, tiktok, youtube, pinterest
 * 
 * The content is automatically copied to clipboard so users can paste it directly
 */
export const shareToPlatform = async (platform: string, options: SocialShareOptions): Promise<void> => {
  // Copy content to clipboard first so users can paste it directly
  if (options.text) {
    await copyToClipboard(options.text);
  }
  
  let url = '';
  
  switch (platform.toLowerCase()) {
    case 'instagram':
      url = getInstagramShareUrl(options);
      break;
    case 'facebook':
      url = getFacebookShareUrl(options);
      break;
    case 'twitter':
    case 'x':
      url = getTwitterShareUrl(options);
      break;
    case 'linkedin':
      url = getLinkedInShareUrl(options);
      break;
    case 'tiktok':
      url = getTikTokShareUrl(options);
      break;
    case 'youtube':
      url = getYouTubeShareUrl(options);
      break;
    case 'pinterest':
      url = getPinterestShareUrl(options);
      break;
    default:
      // console.warn(`Unknown platform: ${platform}`);
      return;
  }
  
  openShareUrl(url);
};

